/**
 * Application Bootstrap Utilities
 * Handles initial configuration loading and session setup
 */

import {
  getEnvironments,
  getNavigation,
  setEnvironmentGlobal,
  createSession,
  setSessionId,
  setAuthType,
  getSessionId,
  getAuthType,
} from '../api';
import { ProgramInformation, NavigationConfig } from '../api/types';

const OHID = 'ohid';
const TESTING = 'testing';
const CODE = 'code';

/**
 * Load and initialize environment configuration
 */
export async function initializeEnvironment(): Promise<ProgramInformation> {
  const env = await getEnvironments();
  setEnvironmentGlobal(env);
  return env;
}

/**
 * Load navigation configuration
 */
export async function initializeNavigation(): Promise<NavigationConfig> {
  const nav = await getNavigation();
  (window as any)['navigation'] = nav;
  return nav;
}

/**
 * Setup no-auth mode for local development
 */
export function setupNoAuthMode(): void {
  const environments = (window as any)['environments'];
  
  if (environments?.no_auth === true) {
    alert('WARNING! NextGen is running without authentication!');
    setAuthType(TESTING);
    setSessionId(TESTING);
  }
}

/**
 * Setup OHID auto-login from client portal redirect
 */
export function setupClientPortalAutoLogin(): void {
  const query = new URLSearchParams(window.location.search);
  const prompt = query.get('prompt');
  const code = query.get(CODE);
  
  // User has come from another application and is already signed in with OHID
  // Suppress the first sign-on screen
  if (prompt) {
    const hasSession = !!getSessionId(); // Use truthiness check instead of !== null
    
    if (!hasSession && !code) {
      // Set auth type to OHID for auto-login
      setAuthType(OHID);
    }
  }
}

/**
 * Initialize default auth type if not set
 */
export function initializeDefaultAuthType(): void {
  const authType = getAuthType();
  
  if (!authType) {
    setAuthType(OHID);
  }
  
  // Store in localStorage for legacy compatibility
  const currentAuthType = getAuthType();
  if (currentAuthType) {
    localStorage.setItem('loginType', currentAuthType);
  }
}

/**
 * Handle OAuth callback with authorization code
 */
export async function handleOAuthCallback(code: string): Promise<string> {
  sessionStorage.removeItem('logout');
  
  // Ensure auth type is set
  if (!getAuthType()) {
    setAuthType(OHID);
  }
  
  const state = new URLSearchParams(window.location.search).get('state');
  const response = state ? await createSession(code, state) : await createSession(code);
  
  setSessionId(response.sessionId);
  
  return response.sessionId;
}

/**
 * Determine which component to render based on auth state
 */
export function getBootstrapState(): 'app' | 'login' {
  const query = new URLSearchParams(window.location.search);
  const code = query.get(CODE);
  const sessionId = getSessionId();
  const isLogout = sessionStorage.getItem('logout');
  
  // Check if user is logging out
  if (isLogout) {
    sessionStorage.removeItem('logout');
    return 'login';
  }
  
  // Check if user has valid session
  if (sessionId) {
    return 'app';
  }
  
  // Check if user is returning from OAuth provider
  if (code) {
    return 'app'; // Will trigger OAuth callback
  }
  
  // No session, no code = show login
  return 'login';
}

/**
 * Check if authentication is enabled
 */
export function isAuthEnabled(): boolean {
  const nav = (window as any)['navigation'];
  return nav?.authentication === 'auth-proxy';
}
